import { Alert, Box, Button, Typography } from '@mui/material';
import { RefreshCw as RefreshIcon } from 'lucide-react';
import { useAppUser } from '@/contexts/AppUserProvider';
import { AuthenticationError } from '@/services/userService';

const AuthErrorHandler = () => {
  const { authError, error, canRetryAuth, retryAuthentication, isLoading } =
    useAppUser();

  if (!authError && !error) {
    return null;
  }

  const getErrorMessage = (): string => {
    if (authError === AuthenticationError.POPUP_BLOCKED) {
      return 'Pop-up windows are blocked by your browser. Please enable pop-ups for this site and try again, or the page will redirect you to log in.';
    }
    if (authError === AuthenticationError.NETWORK_ERROR) {
      return 'Network connection issue. Please check your internet connection and try again.';
    }
    if (
      authError === AuthenticationError.CONSENT_REQUIRED ||
      authError === AuthenticationError.LOGIN_REQUIRED ||
      authError === AuthenticationError.INTERACTION_REQUIRED
    ) {
      return 'Authentication is required. Please log in to continue.';
    }
    return (
      error?.message || 'An authentication error occurred. Please try again.'
    );
  };

  const getErrorTitle = (): string => {
    if (authError === AuthenticationError.POPUP_BLOCKED) {
      return 'Pop-up Blocked';
    }
    if (authError === AuthenticationError.NETWORK_ERROR) {
      return 'Network Error';
    }
    return 'Authentication Error';
  };

  const getSeverity = (): 'error' | 'warning' | 'info' => {
    if (authError === AuthenticationError.POPUP_BLOCKED) {
      return 'warning';
    }
    if (authError === AuthenticationError.NETWORK_ERROR) {
      return 'info';
    }
    return 'error';
  };

  const handleRetry = async () => {
    try {
      await retryAuthentication();
    } catch (err) {
      console.error('Manual retry failed:', err);
    }
  };

  return (
    <Box sx={{ margin: 2, maxWidth: 600, mx: 'auto' }}>
      <Alert severity={getSeverity()}>
        <Typography variant="h6" gutterBottom>
          {getErrorTitle()}
        </Typography>
        <Typography variant="body2" paragraph>
          {getErrorMessage()}
        </Typography>

        {authError === AuthenticationError.POPUP_BLOCKED && (
          <Typography variant="body2" paragraph>
            <strong>How to enable pop-ups:</strong>
            <br />
            • Chrome: Click the pop-up blocked icon in the address bar
            <br />
            • Firefox: Click "Options" in the notification bar
            <br />• Safari: Go to Safari → Preferences → Websites → Pop-up
            Windows
          </Typography>
        )}

        {canRetryAuth && (
          <Button
            variant="contained"
            color="primary"
            onClick={handleRetry}
            disabled={isLoading}
            startIcon={<RefreshIcon size={16} />}
            sx={{ mt: 1 }}
          >
            {isLoading ? 'Retrying...' : 'Try Again'}
          </Button>
        )}
      </Alert>
    </Box>
  );
};

export default AuthErrorHandler;
